/**
 * V57: 평가(Evaluation) 관련 훅
 *
 * 3개 훅:
 * - useMyEvaluations: 내 평가 목록/상세/자가평가 제출
 * - useOrgEvaluations: 조직원 평가 생성/목록/리드평가/팀장평가/HR발송
 * - useHrEvaluations: HR 수신 평가 목록/상세/템플릿 관리
 */

import { useState, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

// ============================================
// 공통 타입 (FE 전용)
// ============================================

export type EvaluationType =
  | 'probation_1m'
  | 'probation_2m'
  | 'probation_3m'
  | 'dispatch_contract'
  | 'dispatch'
  | 'contract'
  | 'annual';

export type EvaluationStatus =
  | 'draft'
  | 'self_submitted'
  | 'lead_submitted'
  | 'manager_submitted'
  | 'second_submitted'
  | 'sent_to_upper'
  | 'sent_to_hr'
  | 'hr_approved'
  | 'hr_rejected'
  | 'rejected';

export type FinalGrade = 'S' | 'A' | 'B' | 'C' | 'D';

export const EVALUATION_TYPE_LABELS: Record<EvaluationType, string> = {
  probation_1m: '수습 1개월차 평가',
  probation_2m: '수습 2개월차 평가',
  probation_3m: '수습 3개월차 평가',
  dispatch_contract: '파견직/계약직 근무평가',
  dispatch: '파견직 근무평가',
  contract: '계약직 근무평가',
  annual: '연말평가',
};

export const EVALUATION_STATUS_LABELS: Record<EvaluationStatus, string> = {
  draft: '평가 대기',
  self_submitted: '자가평가 완료',
  lead_submitted: '리드평가 완료',
  manager_submitted: '최종평가 완료',
  second_submitted: '2차 평가 완료',
  sent_to_upper: '상위 조직장 전달',
  sent_to_hr: '인사팀 승인 대기',
  hr_approved: '인사팀 승인',
  hr_rejected: '인사팀 반려',
  rejected: '상위 조직장 반려',
};

export const FINAL_GRADE_LABELS: Record<FinalGrade, string> = {
  S: 'S (최우수)',
  A: 'A (우수)',
  B: 'B (보통)',
  C: 'C (미흡)',
  D: 'D (부진)',
};

export interface EvaluationSection {
  id: string;
  title: string;
  description?: string;
  inputType: 'rating_5' | 'rating_10' | 'text' | 'select';
  options?: string[];
  requiredFor: ('self' | 'lead' | 'manager')[];
}

// V2 types
export type EvaluationRole = 'self' | 'lead' | 'manager' | 'second';

export interface ScoringScale {
  min: number;
  max: number;
  step: number;
}

export interface EvaluationNode {
  id: string;
  parentId: string | null;
  title: string;
  description?: string;
  order: number;
  isActive: boolean;
}

export interface EvaluationItemV2 {
  id: string;
  nodeId: string;
  title: string;
  description?: string;
  inputType: 'rating' | 'text' | 'select';
  options?: string[];
  weight: number;
  maxPoints?: number;
  requiredFor: EvaluationRole[];
  order: number;
  isActive: boolean;
}

export interface CategoryTotal {
  nodeId: string;
  label: string;
  total: number;
  maxPossible: number;
}

export interface EvaluationTotals {
  byCategory: Record<string, CategoryTotal>;
  grandTotal: number;
  grandMaxPossible: number;
}

export interface EvaluationItem {
  id: string;
  companyCode: string;
  templateId: string;
  templateType: EvaluationType;
  templateTitle: string;
  targetUserId: string;
  targetUserName: string;
  targetUserOrgUnitId: string;
  targetUserOrgUnitName: string;
  targetUserPositionTitle?: string;
  evaluatorId: string;
  evaluatorName: string;
  leadEvaluatorId?: string;
  leadEvaluatorName?: string;
  /** V63.10: 2차 평가자 (계약직) */
  secondEvaluatorId?: string;
  secondEvaluatorName?: string;
  /** V63.11: 상위 조직장 */
  upperEvaluatorId?: string;
  upperEvaluatorName?: string;
  /** V62-fix: 상위 조직장 소속/직책 (getEvaluationDetail에서 보강) */
  upperEvaluatorOrgUnitName?: string;
  upperEvaluatorJobTitle?: string;
  status: EvaluationStatus;
  selfEvaluation?: Record<string, any>;
  selfComment?: string;
  selfSubmittedAt?: string;
  leadEvaluation?: Record<string, any>;
  leadComment?: string;
  leadSubmittedAt?: string;
  managerEvaluation?: Record<string, any>;
  managerComment?: string;
  managerSubmittedAt?: string;
  /** V63.10: 2차 평가 (계약직) */
  secondEvaluation?: Record<string, any>;
  secondComment?: string;
  secondSubmittedAt?: string;
  finalGrade?: FinalGrade;
  /** V63: 세부 등급 문자열 (probation_3m: "A+ (4.3)" 등) */
  finalGradeDetail?: string;
  finalComment?: string;
  sentToHrAt?: string;
  sentToHrBy?: string;
  // V59: HR 결재
  hrDecision?: 'approved' | 'rejected';
  hrComment?: string;
  hrDecisionAt?: string;
  hrDecisionBy?: string;
  evaluationPeriod?: string;
  evaluationYear: number;
  // V2 totals
  selfTotals?: EvaluationTotals;
  leadTotals?: EvaluationTotals;
  managerTotals?: EvaluationTotals;
  /** V63.10: 2차 평가 합산 (계약직) */
  secondTotals?: EvaluationTotals;
  /** V63.10: 가중 합산 총점 (계약직: 1차×0.6 + 2차×0.4) */
  weightedTotalScore?: number;
  templateVersion?: number;
  /** V63: 재평가 사이클 번호 (기본 1) */
  evaluationCycle?: number;
  /** V63: 재평가 원본 3차 평가 ID */
  parentEvaluationId?: string;
  /** V57.3: 평가 생성 시점 템플릿 스냅샷 (BE가 getEvaluationDetail에서 해석 후 templateData로 반환) */
  templateSnapshot?: {
    sections?: any[];
    nodes?: EvaluationNode[];
    items?: EvaluationItemV2[];
    scoringScale?: ScoringScale;
    scoringMethod?: string;
    maxDepth?: number;
  };
  createdAt: string;
  updatedAt: string;
  // getEvaluationDetail에서 추가
  templateSections?: EvaluationSection[];
  // V2 template data (getEvaluationDetail에서 추가)
  templateNodes?: EvaluationNode[];
  templateItems?: EvaluationItemV2[];
  templateScoringScale?: ScoringScale;
  templateScoringMethod?: string;
  templateMaxDepth?: 1 | 2 | 3;
  /** V61: 근태 자동 채움 데이터 */
  attendanceSummary?: {
    lateCount: number;
    earlyLeave: string;
    absenceCount: number;
    note: string;
    periodStart: string;
    periodEnd: string;
    attendedDays: number;
    workingDays: number;
  };
  /** V62.2: 현재 사용자의 평가 내 역할 */
  viewerRole?: 'self' | 'lead' | 'manager' | 'admin';
}

/** V57.6: 템플릿 상태 */
export type TemplateStatus = 'draft' | 'ready';

export interface EvaluationTemplateItem {
  id: string;
  companyCode: string;
  type: EvaluationType;
  title: string;
  version: number;
  isActive: boolean;
  /** V57.6: draft=항목 미완성, ready=항목 완성 */
  templateStatus?: TemplateStatus;
  /** V57.7: 시스템 시드 템플릿 여부 */
  isSystemTemplate?: boolean;
  /** V57.7: 수정 잠금 여부 */
  isLocked?: boolean;
  sections: EvaluationSection[];
  // V2 fields
  maxDepth?: 1 | 2 | 3;
  scoringScale?: ScoringScale;
  scoringMethod?: string;
  nodes?: EvaluationNode[];
  items?: EvaluationItemV2[];
  createdAt: string;
  updatedAt: string;
}

// ============================================
// V57.3+: submit 에러에서 details 활용한 사용자 친화 메시지 구성
// ============================================

function formatValidationError(err: any, fallback: string): string {
  // Firebase HttpsError의 details 필드에서 missingTitles 추출
  const details = err?.details;
  if (details?.missingTitles?.length) {
    const titles = (details.missingTitles as string[]).join(', ');
    return `다음 필수 문항이 누락되었습니다: ${titles}`;
  }
  return err?.message || fallback;
}

// ============================================
// useMyEvaluations
// ============================================

export function useMyEvaluations() {
  const [evaluations, setEvaluations] = useState<EvaluationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const listMyEvaluations = useCallback(async (params?: {
    year?: number;
    status?: EvaluationStatus;
  }) => {
    if (!functions || fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'listMyEvaluations');
      const result = await fn(params || {});
      const data = result.data as any;
      setEvaluations(data.evaluations || []);
    } catch (err: any) {
      console.error('[useMyEvaluations] listMyEvaluations 실패:', err);
      setError(err?.message || '평가 목록 조회 실패');
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  const getEvaluationDetail = useCallback(async (evaluationId: string): Promise<EvaluationItem | null> => {
    if (!functions) return null;
    try {
      const fn = httpsCallable(functions, 'getEvaluationDetail');
      const result = await fn({ evaluationId });
      const data = result.data as any;
      if (!data.evaluation) return null;
      return {
        ...data.evaluation,
        templateSections: data.sections || [],
        templateNodes: data.templateData?.nodes || undefined,
        templateItems: data.templateData?.items || undefined,
        templateScoringScale: data.templateData?.scoringScale || undefined,
        templateScoringMethod: data.templateData?.scoringMethod || undefined,
        templateMaxDepth: data.templateData?.maxDepth || undefined,
        attendanceSummary: data.attendanceSummary || undefined,
        viewerRole: data.viewerRole || undefined,
      };
    } catch (err: any) {
      console.error('[useMyEvaluations] getEvaluationDetail 실패:', err);
      setError(err?.message || '평가 상세 조회 실패');
      return null;
    }
  }, []);

  const submitSelfEvaluation = useCallback(async (params: {
    evaluationId: string;
    selfEvaluation: Record<string, any>;
    selfComment?: string;
  }): Promise<boolean> => {
    if (!functions) return false;
    try {
      const fn = httpsCallable(functions, 'submitSelfEvaluation');
      await fn(params);
      return true;
    } catch (err: any) {
      console.error('[useMyEvaluations] submitSelfEvaluation 실패:', err);
      setError(formatValidationError(err, '자가평가 제출 실패'));
      return false;
    }
  }, []);

  // V58: 멘토(리드)평가 제출 — MyEvaluationsPage에서 사용
  const submitLeadEvaluation = useCallback(async (params: {
    evaluationId: string;
    leadEvaluation: Record<string, any>;
    leadComment?: string;
  }): Promise<boolean> => {
    if (!functions) return false;
    try {
      const fn = httpsCallable(functions, 'submitLeadEvaluation');
      await fn(params);
      return true;
    } catch (err: any) {
      console.error('[useMyEvaluations] submitLeadEvaluation 실패:', err);
      setError(formatValidationError(err, '리드평가 제출 실패'));
      return false;
    }
  }, []);

  return {
    evaluations,
    loading,
    error,
    listMyEvaluations,
    getEvaluationDetail,
    submitSelfEvaluation,
    submitLeadEvaluation,
  };
}

// ============================================
// useOrgEvaluations
// ============================================

export function useOrgEvaluations() {
  const [evaluations, setEvaluations] = useState<EvaluationItem[]>([]);
  const [templates, setTemplates] = useState<EvaluationTemplateItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const listOrgEvaluations = useCallback(async (params?: {
    year?: number;
    status?: EvaluationStatus;
  }) => {
    if (!functions || fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'listOrgEvaluations');
      const result = await fn(params || {});
      const data = result.data as any;
      setEvaluations(data.evaluations || []);
    } catch (err: any) {
      console.error('[useOrgEvaluations] listOrgEvaluations 실패:', err);
      setError(err?.message || '조직 평가 목록 조회 실패');
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  const createEvaluation = useCallback(async (params: {
    targetUserId: string;
    templateId: string;
    evaluationYear: number;
    evaluationPeriod?: string;
    leadEvaluatorId?: string;
  }): Promise<string | null> => {
    if (!functions) return null;
    try {
      const fn = httpsCallable(functions, 'createEvaluation');
      const result = await fn(params);
      const data = result.data as any;
      return data.evaluationId || null;
    } catch (err: any) {
      console.error('[useOrgEvaluations] createEvaluation 실패:', err);
      setError(err?.message || '평가 생성 실패');
      return null;
    }
  }, []);

  const getEvaluationDetail = useCallback(async (evaluationId: string): Promise<EvaluationItem | null> => {
    if (!functions) return null;
    try {
      const fn = httpsCallable(functions, 'getEvaluationDetail');
      const result = await fn({ evaluationId });
      const data = result.data as any;
      if (!data.evaluation) return null;
      return {
        ...data.evaluation,
        templateSections: data.sections || [],
        templateNodes: data.templateData?.nodes || undefined,
        templateItems: data.templateData?.items || undefined,
        templateScoringScale: data.templateData?.scoringScale || undefined,
        templateScoringMethod: data.templateData?.scoringMethod || undefined,
        templateMaxDepth: data.templateData?.maxDepth || undefined,
        viewerRole: data.viewerRole || undefined,
      };
    } catch (err: any) {
      console.error('[useOrgEvaluations] getEvaluationDetail 실패:', err);
      setError(err?.message || '평가 상세 조회 실패');
      return null;
    }
  }, []);

  const submitLeadEvaluation = useCallback(async (params: {
    evaluationId: string;
    leadEvaluation: Record<string, any>;
    leadComment?: string;
  }): Promise<boolean> => {
    if (!functions) return false;
    try {
      const fn = httpsCallable(functions, 'submitLeadEvaluation');
      await fn(params);
      return true;
    } catch (err: any) {
      console.error('[useOrgEvaluations] submitLeadEvaluation 실패:', err);
      setError(formatValidationError(err, '리드평가 제출 실패'));
      return false;
    }
  }, []);

  const submitManagerEvaluation = useCallback(async (params: {
    evaluationId: string;
    managerEvaluation: Record<string, any>;
    managerComment?: string;
    finalGrade?: FinalGrade;
    /** V63: 세부 등급 문자열 */
    finalGradeDetail?: string;
    finalComment?: string;
    /** V64.5: 상위 조직장 ID */
    upperEvaluatorId?: string;
  }): Promise<boolean> => {
    if (!functions) return false;
    try {
      const fn = httpsCallable(functions, 'submitManagerEvaluation');
      await fn(params);
      return true;
    } catch (err: any) {
      console.error('[useOrgEvaluations] submitManagerEvaluation 실패:', err);
      // V281: 에러를 throw하여 컴포넌트 catch에서 모달 내부 에러 표시 가능하도록 변경
      const msg = formatValidationError(err, '팀장 최종평가 제출 실패');
      throw new Error(msg);
    }
  }, []);

  const sendEvaluationToHr = useCallback(async (evaluationId: string): Promise<boolean> => {
    if (!functions) return false;
    try {
      const fn = httpsCallable(functions, 'sendEvaluationToHr');
      await fn({ evaluationId });
      return true;
    } catch (err: any) {
      console.error('[useOrgEvaluations] sendEvaluationToHr 실패:', err);
      setError(err?.message || '인사팀 발송 실패');
      return false;
    }
  }, []);

  const listEvaluationTemplates = useCallback(async (params?: {
    companyCode?: string;
    type?: EvaluationType;
    activeOnly?: boolean;
  }) => {
    if (!functions) return;
    try {
      const fn = httpsCallable(functions, 'listEvaluationTemplates');
      const result = await fn(params || { activeOnly: true });
      const data = result.data as any;
      setTemplates(data.templates || []);
    } catch (err: any) {
      console.error('[useOrgEvaluations] listEvaluationTemplates 실패:', err);
      setError(err?.message || '템플릿 목록 조회 실패');
    }
  }, []);

  // V59: 수습 평가 HR 제출
  const sendProbationToHr = useCallback(async (evaluationId: string): Promise<boolean> => {
    if (!functions) return false;
    try {
      const fn = httpsCallable(functions, 'sendProbationToHr');
      await fn({ evaluationId });
      return true;
    } catch (err: any) {
      console.error('[useOrgEvaluations] sendProbationToHr 실패:', err);
      setError(err?.message || '수습 평가 인사팀 제출 실패');
      return false;
    }
  }, []);

  // V58+V63: 수습 평가 자동 생성 (차수 자동 감지 + 시스템 템플릿 자동 선택 + 재평가 사이클)
  const createProbationEvaluation = useCallback(async (params: {
    targetUserId: string;
    leadEvaluatorId: string;
    /** V63: 재평가 사이클 번호 (기본 1) */
    evaluationCycle?: number;
    /** V63: 재평가 원본 3차 평가 ID */
    parentEvaluationId?: string;
  }): Promise<{ evaluationId: string; round: number } | null> => {
    if (!functions) return null;
    try {
      const fn = httpsCallable(functions, 'createProbationEvaluation');
      const result = await fn(params);
      const data = result.data as any;
      return { evaluationId: data.evaluationId, round: data.round };
    } catch (err: any) {
      console.error('[useOrgEvaluations] createProbationEvaluation 실패:', err);
      setError(err?.message || '수습 평가 생성 실패');
      return null;
    }
  }, []);

  // V62: 직원 평가 이력
  const [employeeEvaluations, setEmployeeEvaluations] = useState<EvaluationItem[]>([]);
  const [employeeEvalsLoading, setEmployeeEvalsLoading] = useState(false);

  const listEmployeeEvaluations = useCallback(async (targetUserId: string) => {
    if (!functions) return;
    setEmployeeEvalsLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'listEmployeeEvaluations');
      const result = await fn({ targetUserId });
      const data = result.data as any;
      setEmployeeEvaluations((data.evaluations || []).map((e: any) => ({
        id: e.id,
        templateType: e.templateType,
        templateName: e.templateName,
        evaluationYear: e.evaluationYear,
        status: e.status,
        targetUserId: e.targetUserId,
        targetUserName: e.targetUserName,
        evaluatorId: e.evaluatorId,
        evaluatorName: e.evaluatorName,
        createdAt: e.createdAt,
        updatedAt: e.updatedAt,
        round: e.round,
        attendanceSummary: e.attendanceSummary || undefined,
      })));
    } catch (err: any) {
      console.error('[useOrgEvaluations] listEmployeeEvaluations 실패:', err);
      setError(err?.message || '직원 평가 이력 조회 실패');
      setEmployeeEvaluations([]);
    } finally {
      setEmployeeEvalsLoading(false);
    }
  }, []);

  // V63.10: 파견/계약직 근무평가 생성
  const createWorkEvaluation = useCallback(async (params: {
    targetUserId: string;
    evaluationYear: number;
    evaluationPeriod?: string;
    secondEvaluatorId?: string;
    upperEvaluatorId?: string;
    templateTypeOverride?: 'dispatch' | 'contract'; // V64.3
  }): Promise<{ evaluationId: string; templateType: EvaluationType; secondEvaluatorId?: string } | null> => {
    if (!functions) return null;
    try {
      const fn = httpsCallable(functions, 'createWorkEvaluation');
      const result = await fn(params);
      const data = result.data as any;
      return { evaluationId: data.evaluationId, templateType: data.templateType, secondEvaluatorId: data.secondEvaluatorId };
    } catch (err: any) {
      console.error('[useOrgEvaluations] createWorkEvaluation 실패:', err);
      setError(err?.message || '근무평가 생성 실패');
      throw err;
    }
  }, []);

  // V63.10: 계약직 2차 평가 제출
  const submitSecondEvaluation = useCallback(async (params: {
    evaluationId: string;
    secondEvaluation: Record<string, any>;
    secondComment?: string;
  }): Promise<boolean> => {
    if (!functions) return false;
    try {
      const fn = httpsCallable(functions, 'submitSecondEvaluation');
      await fn(params);
      return true;
    } catch (err: any) {
      console.error('[useOrgEvaluations] submitSecondEvaluation 실패:', err);
      setError(formatValidationError(err, '2차 평가 제출 실패'));
      return false;
    }
  }, []);

  // V63.10: 파견/계약직 HR 승인/반려
  const finalizeWorkEvaluation = useCallback(async (params: {
    evaluationId: string;
    decision: 'approved' | 'rejected';
    hrComment?: string;
  }): Promise<boolean> => {
    if (!functions) return false;
    try {
      const fn = httpsCallable(functions, 'finalizeWorkEvaluation');
      await fn(params);
      return true;
    } catch (err: any) {
      console.error('[useOrgEvaluations] finalizeWorkEvaluation 실패:', err);
      setError(err?.message || '인사팀 결재 실패');
      return false;
    }
  }, []);

  // V307: 상위 조직장 컨펌 요청 목록 (타 조직 포함)
  const [upperConfirmRequests, setUpperConfirmRequests] = useState<EvaluationItem[]>([]);
  const [upperConfirmLoading, setUpperConfirmLoading] = useState(false);
  const upperConfirmFetchingRef = useRef(false);

  const listUpperConfirmRequests = useCallback(async (params?: { year?: number }) => {
    if (!functions || upperConfirmFetchingRef.current) return;
    upperConfirmFetchingRef.current = true;
    setUpperConfirmLoading(true);
    try {
      const fn = httpsCallable(functions, 'listUpperConfirmRequests');
      const result = await fn(params || {});
      const data = result.data as any;
      setUpperConfirmRequests(data.evaluations || []);
    } catch (err: any) {
      console.error('[useOrgEvaluations] listUpperConfirmRequests 실패:', err);
      setError(err?.message || '컨펌 요청 목록 조회 실패');
    } finally {
      setUpperConfirmLoading(false);
      upperConfirmFetchingRef.current = false;
    }
  }, []);

  // V63.11: 상위 조직장에게 전달
  const sendWorkEvalToUpper = useCallback(async (evaluationId: string): Promise<boolean> => {
    if (!functions) return false;
    try {
      const fn = httpsCallable(functions, 'sendWorkEvalToUpper');
      await fn({ evaluationId });
      return true;
    } catch (err: any) {
      console.error('[useOrgEvaluations] sendWorkEvalToUpper 실패:', err);
      setError(formatValidationError(err, '상위 조직장 전달 실패'));
      return false;
    }
  }, []);

  // V63.11: 상위 조직장 승인/반려
  const approveWorkEvaluation = useCallback(async (params: {
    evaluationId: string;
    decision: 'approved' | 'rejected';
    comment?: string;
  }): Promise<boolean> => {
    if (!functions) return false;
    try {
      const fn = httpsCallable(functions, 'approveWorkEvaluation');
      await fn(params);
      return true;
    } catch (err: any) {
      console.error('[useOrgEvaluations] approveWorkEvaluation 실패:', err);
      setError(formatValidationError(err, '상위 조직장 결재 실패'));
      return false;
    }
  }, []);

  return {
    evaluations,
    templates,
    loading,
    error,
    listOrgEvaluations,
    createEvaluation,
    createProbationEvaluation,
    getEvaluationDetail,
    submitLeadEvaluation,
    submitManagerEvaluation,
    sendEvaluationToHr,
    sendProbationToHr,
    listEvaluationTemplates,
    employeeEvaluations,
    employeeEvalsLoading,
    listEmployeeEvaluations,
    // V63.10
    createWorkEvaluation,
    submitSecondEvaluation,
    finalizeWorkEvaluation,
    // V63.11
    sendWorkEvalToUpper,
    approveWorkEvaluation,
    // V307
    upperConfirmRequests,
    upperConfirmLoading,
    listUpperConfirmRequests,
  };
}

// ============================================
// useHrEvaluations
// ============================================

export function useHrEvaluations() {
  const [evaluations, setEvaluations] = useState<EvaluationItem[]>([]);
  const [templates, setTemplates] = useState<EvaluationTemplateItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const listHrEvaluations = useCallback(async (params?: {
    companyCode?: string;
    year?: number;
    status?: EvaluationStatus;
  }) => {
    if (!functions || fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'listHrEvaluations');
      const result = await fn(params || {});
      const data = result.data as any;
      setEvaluations(data.evaluations || []);
    } catch (err: any) {
      console.error('[useHrEvaluations] listHrEvaluations 실패:', err);
      setError(err?.message || '인사팀 평가 목록 조회 실패');
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  const getEvaluationDetail = useCallback(async (evaluationId: string): Promise<EvaluationItem | null> => {
    if (!functions) return null;
    try {
      const fn = httpsCallable(functions, 'getEvaluationDetail');
      const result = await fn({ evaluationId });
      const data = result.data as any;
      if (!data.evaluation) return null;
      return {
        ...data.evaluation,
        templateSections: data.sections || [],
        templateNodes: data.templateData?.nodes || undefined,
        templateItems: data.templateData?.items || undefined,
        templateScoringScale: data.templateData?.scoringScale || undefined,
        templateScoringMethod: data.templateData?.scoringMethod || undefined,
        templateMaxDepth: data.templateData?.maxDepth || undefined,
        viewerRole: data.viewerRole || undefined,
      };
    } catch (err: any) {
      console.error('[useHrEvaluations] getEvaluationDetail 실패:', err);
      setError(err?.message || '평가 상세 조회 실패');
      return null;
    }
  }, []);

  const listEvaluationTemplates = useCallback(async (params?: {
    companyCode?: string;
    type?: EvaluationType;
    activeOnly?: boolean;
  }) => {
    if (!functions) return;
    try {
      const fn = httpsCallable(functions, 'listEvaluationTemplates');
      const result = await fn(params || {});
      const data = result.data as any;
      setTemplates(data.templates || []);
    } catch (err: any) {
      console.error('[useHrEvaluations] listEvaluationTemplates 실패:', err);
      setError(err?.message || '템플릿 목록 조회 실패');
    }
  }, []);

  const createEvaluationTemplate = useCallback(async (params: {
    companyCode?: string;
    type: EvaluationType;
    title: string;
    sections?: EvaluationSection[];
    // V2 optional
    maxDepth?: 1 | 2 | 3;
    scoringScale?: ScoringScale;
    scoringMethod?: string;
    nodes?: EvaluationNode[];
    items?: EvaluationItemV2[];
  }): Promise<string | null> => {
    if (!functions) return null;
    try {
      const fn = httpsCallable(functions, 'createEvaluationTemplate');
      const result = await fn(params);
      const data = result.data as any;
      return data.templateId || null;
    } catch (err: any) {
      console.error('[useHrEvaluations] createEvaluationTemplate 실패:', err);
      setError(err?.message || '템플릿 생성 실패');
      return null;
    }
  }, []);

  const updateEvaluationTemplate = useCallback(async (params: {
    templateId: string;
    title?: string;
    sections?: EvaluationSection[];
    isActive?: boolean;
    // V2 optional
    maxDepth?: 1 | 2 | 3;
    scoringScale?: ScoringScale;
    scoringMethod?: string;
    nodes?: EvaluationNode[];
    items?: EvaluationItemV2[];
  }): Promise<boolean> => {
    if (!functions) return false;
    try {
      const fn = httpsCallable(functions, 'updateEvaluationTemplate');
      await fn(params);
      return true;
    } catch (err: any) {
      console.error('[useHrEvaluations] updateEvaluationTemplate 실패:', err);
      setError(err?.message || '템플릿 수정 실패');
      return false;
    }
  }, []);

  // V59: 수습 평가 HR 승인/반려
  const finalizeProbationEvaluation = useCallback(async (params: {
    evaluationId: string;
    decision: 'approved' | 'rejected';
    hrComment?: string;
  }): Promise<boolean> => {
    if (!functions) return false;
    try {
      const fn = httpsCallable(functions, 'finalizeProbationEvaluation');
      await fn(params);
      return true;
    } catch (err: any) {
      console.error('[useHrEvaluations] finalizeProbationEvaluation 실패:', err);
      setError(err?.message || '인사팀 결재 실패');
      return false;
    }
  }, []);

  // V72: 근무평가 HR 승인/반려
  const finalizeWorkEvaluation = useCallback(async (params: {
    evaluationId: string;
    decision: 'approved' | 'rejected';
    hrComment?: string;
  }): Promise<boolean> => {
    if (!functions) return false;
    try {
      const fn = httpsCallable(functions, 'finalizeWorkEvaluation');
      await fn(params);
      return true;
    } catch (err: any) {
      console.error('[useHrEvaluations] finalizeWorkEvaluation 실패:', err);
      setError(err?.message || '인사팀 결재 실패');
      return false;
    }
  }, []);

  // V57.7: 시스템 고정 템플릿 시드
  const seedSystemEvaluationTemplates = useCallback(async (companyCode: string): Promise<{ success: boolean; created: number; skipped: number; message: string } | null> => {
    if (!functions) return null;
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'seedSystemEvaluationTemplates');
      const result = await fn({ companyCode });
      return result.data as { success: boolean; created: number; skipped: number; message: string };
    } catch (err: any) {
      console.error('[useHrEvaluations] seedSystemEvaluationTemplates 실패:', err);
      setError(err?.message || '시스템 템플릿 시드 실패');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    evaluations,
    templates,
    loading,
    error,
    listHrEvaluations,
    getEvaluationDetail,
    finalizeProbationEvaluation,
    finalizeWorkEvaluation,
    listEvaluationTemplates,
    createEvaluationTemplate,
    updateEvaluationTemplate,
    seedSystemEvaluationTemplates,
  };
}
